import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { AutocompleteAddressField, SelectField, TextField } from '@roc/ui';
import { borrowerStatesAbbreviation, borrowerStatesNames, parseAddress } from '@roc/feature-utils';
import { IdentityFormStore } from './identityFormStore';
import { ADDRESS_PARTS, AddressValue, REQUIRED_ADDRESS_PARTS, formatAddressLine } from './identityReviewValues';

export const stateOptions = borrowerStatesAbbreviation.map((abbreviation, i) => ({
  value: abbreviation,
  label: borrowerStatesNames[i],
}));

export const IdentityAddressField = observer(({ store }: { store: IdentityFormStore }) => {
  const address: AddressValue = store.values.address ?? {};
  const errorOf = (key: string) => store.form.fields[key]?.error;
  const onChange = (value: AddressValue) => store.onEditFieldChange('address', value);

  const handleAddressChange = event => {
    const { value: line, fullInfo } = event?.target || {};
    const { address_components, geometry } = fullInfo || {};
    const parsed = parseAddress(line, address_components, geometry);
    onChange({
      ...address,
      address: line,
      streetNumber: parsed?.street_number ?? '',
      streetName: parsed?.street ?? '',
      city: parsed?.city ?? '',
      state: parsed?.state ?? '',
      zipCode: parsed?.zip ?? '',
    });
  };

  const handlePartChange = (key: keyof AddressValue, partValue: string) => {
    const next = { ...address, [key]: partValue };
    onChange({ ...next, address: formatAddressLine(next) });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <AutocompleteAddressField
          label="Address"
          name="address"
          testId="address"
          value={address.address ?? formatAddressLine(address)}
          onChange={handleAddressChange}
          required
          standaloneLabel
          fullWidth
        />
      </Grid>
      {ADDRESS_PARTS.map(part => (
        <Grid item xs={12} sm={6} key={part.key}>
          {part.key === 'state' ? (
            <SelectField
              label={part.label}
              value={address[part.key] ?? ''}
              onChange={partValue => handlePartChange(part.key, partValue)}
              options={stateOptions}
              name={part.key}
              testId={part.key}
              variant="outlined"
              fullWidth
              standaloneLabel
              required={REQUIRED_ADDRESS_PARTS.includes(part.key)}
              error={!!errorOf(part.key)}
              helperText={errorOf(part.key)}
            />
          ) : (
            <TextField
              label={part.label}
              value={address[part.key] ?? ''}
              onChange={e => handlePartChange(part.key, e.target.value)}
              name={part.key}
              testId={part.key}
              variant="outlined"
              fullWidth
              standaloneLabel
              required={REQUIRED_ADDRESS_PARTS.includes(part.key)}
              error={!!errorOf(part.key)}
              helperText={errorOf(part.key)}
            />
          )}
        </Grid>
      ))}
    </Grid>
  );
});
